import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { LoadingScreen } from "../../components/LoadingScreen";
import { ApiError, authApi } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatProviderReviewNote } from "../provider-workspace/provider-review-note";
import { useAppPreferences } from "../../lib/preferences";

const emptyForm = {
  business_name: "",
  contact_name: "",
  email: "",
  password: "",
  city: "",
  contact_phone: "",
  application_note: "",
};

export function ProviderRegistrationPage() {
  const { t } = useAppPreferences();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { homePath, registerProvider, resubmitProviderRegistration, status, user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState<string | null>(null);
  const [registrationLoaded, setRegistrationLoaded] = useState(false);
  const [registrationLoadError, setRegistrationLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const submissionPending = useRef(false);
  const [loadingRegistration, setLoadingRegistration] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isResubmissionMode = searchParams.get("mode") === "resubmit";

  useEffect(() => {
    if (status === "authenticated" && user && !isResubmissionMode) {
      navigate(homePath, { replace: true });
    }
    if (status === "anonymous" && isResubmissionMode) {
      navigate("/sign-in", { replace: true });
    }
  }, [homePath, isResubmissionMode, navigate, status, user]);

  useEffect(() => {
    if (!isResubmissionMode || status !== "authenticated" || !user) {
      return;
    }

    let isActive = true;
    setLoadingRegistration(true);
    setRegistrationLoaded(false);
    setRegistrationLoadError(null);

    void authApi
      .getProviderRegistration()
      .then((registration) => {
        if (!isActive) {
          return;
        }

        if (registration.approval_status !== "rejected") {
          navigate(homePath, { replace: true });
          return;
        }

        setForm({
          business_name: registration.business_name,
          contact_name: registration.contact_name,
          email: registration.email,
          password: "",
          city: registration.city,
          contact_phone: registration.contact_phone ?? "",
          application_note: registration.application_note ?? "",
        });
        setReviewNote(registration.review_note);
        setRegistrationLoaded(true);
      })
      .catch((requestError) => {
        if (!isActive) {
          return;
        }

        if (requestError instanceof ApiError) {
          setRegistrationLoadError(requestError.detail);
        } else {
          setRegistrationLoadError(t("Unable to load provider registration."));
        }
      })
      .finally(() => {
        if (isActive) {
          setLoadingRegistration(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [homePath, isResubmissionMode, loadAttempt, navigate, status, user]);

  const updateField = (field: keyof typeof emptyForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submissionPending.current || (isResubmissionMode && !registrationLoaded)) return;
    submissionPending.current = true;
    setSubmitting(true);
    setError(null);

    try {
      if (isResubmissionMode) {
        const session = await resubmitProviderRegistration({
          business_name: form.business_name,
          contact_name: form.contact_name,
          email: form.email,
          city: form.city,
          contact_phone: form.contact_phone || null,
          application_note: form.application_note || null,
        });
        navigate(session.home_path, { replace: true });
      } else {
        const result = await registerProvider({
          business_name: form.business_name,
          contact_name: form.contact_name,
          email: form.email,
          password: form.password,
          city: form.city,
          contact_phone: form.contact_phone || null,
          application_note: form.application_note || null,
        });
        if ("status" in result) {
          navigate(`/verify-email?email=${encodeURIComponent(result.email)}`, { replace: true });
        } else {
          navigate(result.home_path, { replace: true });
        }
      }
    } catch (requestError) {
      if (requestError instanceof ApiError) {
        setError(requestError.detail);
      } else {
        setError(t("Unable to submit provider registration."));
      }
    } finally {
      submissionPending.current = false;
      setSubmitting(false);
    }
  };

  if (status === "loading" || loadingRegistration || (isResubmissionMode && !registrationLoaded && !registrationLoadError)) {
    return <LoadingScreen />;
  }

  if (isResubmissionMode && registrationLoadError) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-5 px-5 py-10">
        <h1 className="text-2xl font-bold">{t("Load your registration")}</h1>
        <p role="alert" className="ops-copy">{registrationLoadError}</p>
        <p className="ops-copy">{t("Your submitted details are safe. Load them before making changes.")}</p>
        <button className="ops-button" data-variant="primary" onClick={() => setLoadAttempt((attempt) => attempt + 1)} type="button">{t("Retry")}</button>
        <Link className="ops-button" data-variant="secondary" to="/provider">{t("Back to workspace")}</Link>
      </main>
    );
  }

  return (
    <div className="flex min-h-screen items-start justify-center px-4 py-10">
      <form
        className="w-full max-w-md space-y-5"
        onSubmit={handleSubmit}
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white">
            {isResubmissionMode ? t("Resubmit registration") : t("Register your venue")}
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            {isResubmissionMode
              ? t("Update and resend for admin review.")
              : t("We'll review your details and set up your workspace.")}
          </p>
        </div>

        {!isResubmissionMode && (
          <p className="ops-copy border-y border-[var(--color-border)] py-3 text-center text-sm">
            {t("Verify your email, get your application reviewed, then set up your venues.")}
          </p>
        )}

        {isResubmissionMode && reviewNote ? (
          <div className="rounded-lg border border-amber-300/25 bg-amber-300/10 px-4 py-3 text-sm text-[var(--color-text)]">
            <p className="font-medium text-amber-200">{t("Admin feedback")}</p>
            <p className="mt-1 whitespace-pre-wrap break-words">{formatProviderReviewNote(reviewNote, t)}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-medium text-slate-400">{t("Business name")}</span>
            <input
              className="ops-input w-full"
              autoComplete="organization"
              maxLength={255}
              minLength={2}
              onChange={(e) => updateField("business_name", e.target.value)}
              placeholder={t("Downtown Arena")}
              required
              value={form.business_name}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-400">{t("Contact name")}</span>
            <input
              className="ops-input w-full"
              autoComplete="name"
              maxLength={255}
              minLength={2}
              onChange={(e) => updateField("contact_name", e.target.value)}
              placeholder={t("Your name")}
              required
              value={form.contact_name}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-400">{t("City")}</span>
            <input
              className="ops-input w-full"
              autoComplete="address-level2"
              maxLength={128}
              minLength={2}
              onChange={(e) => updateField("city", e.target.value)}
              placeholder="Cairo"
              required
              value={form.city}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-medium text-slate-400">{t("Email")}</span>
            <input
              autoComplete="email"
              className="ops-input w-full"
              onChange={(e) => updateField("email", e.target.value)}
              placeholder="owner@example.com"
              required
              type="email"
              value={form.email}
            />
          </label>
          {isResubmissionMode ? null : (
            <label className="block sm:col-span-2">
              <span className="mb-1.5 block text-xs font-medium text-slate-400">{t("Password")}</span>
              <input
                autoComplete="new-password"
                className="ops-input w-full"
                maxLength={128}
                minLength={8}
                onChange={(e) => updateField("password", e.target.value)}
                placeholder={t("At least 8 characters")}
                required
                type="password"
                value={form.password}
              />
            </label>
          )}
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-medium text-slate-400">{t("Phone (optional)")}</span>
            <input
              className="ops-input w-full"
              autoComplete="tel"
              type="tel"
              dir="ltr"
              maxLength={32}
              onChange={(e) => updateField("contact_phone", e.target.value)}
              placeholder="+20..."
              value={form.contact_phone}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-medium text-slate-400">{t("Application note (optional)")}</span>
            <textarea
              className="ops-input min-h-[80px] w-full"
              maxLength={1000}
              onChange={(e) => updateField("application_note", e.target.value)}
              placeholder={t("Venue type, expected units, launch timeline...")}
              value={form.application_note}
            />
          </label>
        </div>

        {error ? (
          <div className="rounded-lg border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100" role="alert">
            {error}
          </div>
        ) : null}

        <button
          className="ops-button w-full"
          data-variant="primary"
          disabled={submitting}
          type="submit"
        >
          {submitting
            ? t("Submitting...")
            : isResubmissionMode
              ? t("Resubmit")
              : t("Submit registration")}
        </button>

        <p className="text-center text-xs text-slate-400">
          {isResubmissionMode ? (
            <Link className="ops-action-link text-slate-300" to="/provider">
              {t("Back to workspace")}
            </Link>
          ) : (
            <>
              {t("Already registered?")} {" "}
              <Link className="ops-action-link text-slate-300" to="/sign-in">
                {t("Sign in")}
              </Link>
            </>
          )}
        </p>
      </form>
    </div>
  );
}
